/**
 * Reusable utilities for cheating detection logic
 * 
 * These utilities provide a centralized way to detect and flag suspicious activity
 * based on cheating detection scores from the assessment data.
 */

import { CandidateAssessmentSummary, CandidateQuestionsAnswersResponse } from '../types/job.types';
import type { TFunction } from 'i18next';
import { getSkillTooltips, SkillTooltipData } from '../hooks/useAssessmentData';

/**
 * Threshold score for considering cheating activity as suspicious
 * Questions with cheatinglikelihood_score >= this value are flagged
 */
export const CHEATING_THRESHOLD = 80;

/**
 * Normalize strings for flexible matching (removes non-alphanumeric, lowercases)
 * 
 * @param str - String to normalize
 * @returns Normalized string (empty string if input is falsy)
 */
export const normalizeString = (str: string): string => {
  if (!str || typeof str !== 'string') {
    return '';
  }
  return str.toLowerCase().replace(/[^a-z0-9]/g, '');
};

/**
 * Get set of normalized skill names that have suspicious activity
 * 
 * @param questionsAnswers - Questions and answers data
 * @param assessment - The candidate assessment summary with cheating detection data
 * @returns Set of normalized skill names (question_kind) that have suspicious activity
 */
export const getSuspiciousSkillsNormalized = (
  questionsAnswers?: CandidateQuestionsAnswersResponse,
  assessment?: CandidateAssessmentSummary | null
): Set<string> => {
  const skills = new Set<string>();
  
  if (!questionsAnswers?.questions_answers || !assessment?.cheating_detection) {
    return skills;
  }

  questionsAnswers.questions_answers.forEach((qa, index) => {
    const cheating = assessment.cheating_detection?.[String(index)];
    const skillName = qa.question_kind || qa.skill_area;
    if (
      cheating &&
      typeof cheating.cheatinglikelihood_score === 'number' &&
      cheating.cheatinglikelihood_score >= CHEATING_THRESHOLD &&
      skillName
    ) {
      const normalizedSkill = normalizeString(skillName);
      if (normalizedSkill) {
        skills.add(normalizedSkill);
      }
    }
  });

  return skills;
};

/**
 * Check if there is any technical cheating detected
 * 
 * @param questionsAnswers - Questions and answers data
 * @param assessment - The candidate assessment summary with cheating detection data
 * @returns true if any technical question has cheating score >= threshold
 */
export const hasTechnicalCheating = (
  questionsAnswers?: CandidateQuestionsAnswersResponse,
  assessment?: CandidateAssessmentSummary | null
): boolean => {
  if (!questionsAnswers?.questions_answers || !assessment?.cheating_detection) {
    return false;
  }

  return questionsAnswers.questions_answers.some((qa, index) => {
    const cheating = assessment.cheating_detection?.[String(index)];
    return (
      qa.section === 'Technical Skills' &&
      cheating &&
      typeof cheating.cheatinglikelihood_score === 'number' &&
      cheating.cheatinglikelihood_score >= CHEATING_THRESHOLD
    );
  });
};

/**
 * Check if there is any soft skills cheating detected
 *
 * @param questionsAnswers - Questions and answers data
 * @param assessment - The candidate assessment summary with cheating detection data
 * @returns true if any soft skills question has cheating score >= threshold
 */
export const hasSoftCheating = (
  questionsAnswers?: CandidateQuestionsAnswersResponse,
  assessment?: CandidateAssessmentSummary | null
): boolean => {
  if (!questionsAnswers?.questions_answers || !assessment?.cheating_detection) {
    return false;
  }

  return questionsAnswers.questions_answers.some((qa, index) => {
    const cheating = assessment.cheating_detection?.[String(index)];
    return (
      qa.section === 'Soft Skills' &&
      cheating &&
      typeof cheating.cheatinglikelihood_score === 'number' &&
      cheating.cheatinglikelihood_score >= CHEATING_THRESHOLD
    );
  });
};

/**
 * Check if there is any cheating detected (technical or soft)
 * 
 * @param questionsAnswers - Questions and answers data
 * @param assessment - The candidate assessment summary with cheating detection data
 * @returns true if any question (technical or soft) has cheating score >= threshold
 */
export const hasAnyCheating = (
  questionsAnswers?: CandidateQuestionsAnswersResponse,
  assessment?: CandidateAssessmentSummary | null
): boolean => {
  return hasTechnicalCheating(questionsAnswers, assessment) || hasSoftCheating(questionsAnswers, assessment);
};

/**
 * Get tooltips with suspicion flags added
 * 
 * @param assessment - The candidate assessment summary
 * @param chartType - Type of chart ('technical' or 'soft')
 * @param questionsAnswers - Optional questions and answers data for cheating detection
 * @returns Tooltips dictionary with suspicion flags added to matching skills
 */
export const getTooltipsWithSuspicion = (
  assessment: CandidateAssessmentSummary | null | undefined,
  chartType: 'technical' | 'soft',
  questionsAnswers?: CandidateQuestionsAnswersResponse,
  tInferred?: TFunction<'candidates'>,
): { [skillName: string]: Array<SkillTooltipData & { hasSuspiciousActivity?: boolean }> } => {
  if (!assessment) {
    return {};
  }

  const section = chartType === 'technical' ? 'Technical Skills' : 'Soft Skills';
  const tooltips = getSkillTooltips(assessment, section, tInferred);
  const suspiciousSkillsNormalized = getSuspiciousSkillsNormalized(questionsAnswers, assessment);

  // Early return if no suspicious skills found
  if (suspiciousSkillsNormalized.size === 0) {
    return tooltips;
  }

  Object.keys(tooltips).forEach(skillKey => {
    const normalizedKey = normalizeString(skillKey);
    if (!normalizedKey) {
      return;
    }

    // Check if any suspicious skill matches (contains or is contained by) the normalized key
    const isSuspicious = Array.from(suspiciousSkillsNormalized).some(susSkill =>
      susSkill && (susSkill.includes(normalizedKey) || normalizedKey.includes(susSkill))
    );

    if (isSuspicious) {
      tooltips[skillKey] = tooltips[skillKey].map((t: SkillTooltipData) => ({
        ...t,
        hasSuspiciousActivity: true
      }));
    }
  });

  return tooltips;
};

/**
 * Enrich skills data with suspicion flags based on normalized skill name matching
 * 
 * @param skills - Array of skill data objects with name and value
 * @param assessment - The candidate assessment summary
 * @param questionsAnswers - Optional questions and answers data for cheating detection
 * @returns Skills array with hasSuspiciousActivity flag added where applicable
 */
export const enrichSkillsWithSuspicion = (
  skills: Array<{ name: string; value: number }>,
  assessment: CandidateAssessmentSummary | null | undefined,
  questionsAnswers?: CandidateQuestionsAnswersResponse
): Array<{ name: string; value: number; hasSuspiciousActivity?: boolean }> => {
  if (!skills || skills.length === 0) {
    return [];
  }

  const suspiciousSkillsNormalized = getSuspiciousSkillsNormalized(questionsAnswers, assessment);

  // Early return if no suspicious skills found
  if (suspiciousSkillsNormalized.size === 0) {
    return skills;
  }

  return skills.map(skill => {
    if (!skill.name) {
      return skill;
    }

    const normalizedKey = normalizeString(skill.name);
    if (!normalizedKey) {
      return skill;
    }

    const isSuspicious = Array.from(suspiciousSkillsNormalized).some(susSkill =>
      susSkill && (susSkill.includes(normalizedKey) || normalizedKey.includes(susSkill))
    );

    return {
      ...skill,
      hasSuspiciousActivity: isSuspicious
    };
  });
};
